<template>
  <div
    v-if="canRender"
    :class="['refapp-video', { 'refapp-video--high-contrast-text': highContrastTextEnabled }]"
  >
    <video
      ref="refappVideo"
      :autoplay="autoplay"
      :muted="autoplay ? true : muted"
      :loop="loop"
      :aria-label="heading"
      class="refapp-video__media"
      :poster="poster"
      disablePictureInPicture
      playsinline
      data-refapp-tagging-target="video"
    >
      <source
        v-for="s in sources"
        :key="s.src"
        :src="s.src"
        :type="s.type"
        :media="getSourceMediaQuery(s.screenSize)"
      />
    </video>

    <refapp-video-content><slot></slot></refapp-video-content>

    <refapp-video-controls @playback="togglePlayback" @audio="toggleAudio" />
  </div>
</template>

<script>
import RefappVideoContent from './content.vue';
import RefappVideoControls from './controls.vue';

const PLAYBACK_CLASS = 'refapp-video__controls-playback--paused';

// sm (360p) | md (720p) | lg (1080p+)
const mediaQueries = {
  sm: '(max-width:640px)',
  md: '(max-width:1280px)',
  lg: null,
};

export default {
  name: 'refapp-video-base',
  components: {
    RefappVideoControls,
    RefappVideoContent,
  },
  inject: [
    'sources',
    'autoplay',
    'muted',
    'loop',
    'poster',
    'controls',
    'taggingId',
    'highContrastTextEnabled',
    'a11yLabels',
    'heading',
  ],
  computed: {
    canRender() {
      return this.sources.some((src) => !!src);
    },
  },
  mounted() {
    const { refappVideo: video } = this.$refs;

    if (!video) return;

    // Query for playback controls within this component's scope
    const playbackControls = this.$el.querySelector('.refapp-video__controls-playback');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            video.pause();
            playbackControls?.classList.add(PLAYBACK_CLASS);
            return;
          }

          if (this.autoplay) {
            video.play();
            playbackControls?.classList.remove(PLAYBACK_CLASS);
          }
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    this.observer = observer;

    observer.observe(video);
  },

  unmounted() {
    const { refappVideo: video } = this.$refs;
    video && this.observer.unobserve(video);
  },

  methods: {
    togglePlayback(button) {
      const { refappVideo: video } = this.$refs;
      const isPlaybackStopped = video.paused || video.stopped;

      const PLAYBACK_MODIFIER_CLASS = 'refapp-video__controls-playback--paused';
      button.classList[isPlaybackStopped ? 'remove' : 'add'](PLAYBACK_MODIFIER_CLASS);
      button.setAttribute('aria-label', this.a11yLabels[isPlaybackStopped ? 'pause' : 'play']);

      isPlaybackStopped ? video.play() : video.pause();
    },

    toggleAudio(button) {
      const { refappVideo: video } = this.$refs;

      const AUDIO_MODIFIER_CLASS = 'refapp-video__controls-audio--muted';
      button.classList[video.muted ? 'remove' : 'add'](AUDIO_MODIFIER_CLASS);
      button.setAttribute('aria-label', this.a11yLabels[video.muted ? 'mute' : 'unmute']);

      video.muted = !video.muted;
    },

    getSourceMediaQuery(size) {
      // do not apply a mediaquery if the requested size is not interpretable
      if (!(size in mediaQueries)) return null;
      return mediaQueries[size];
    },
  },
};
</script>
